import * as Sentry from '@sentry/react';
import { browserTracingIntegration } from '@sentry/react';
import { scrubbedBeforeSend } from './dataScrubbing';

/**
 * Initialize Sentry for React frontend
 * Includes error tracking and performance monitoring. Session replay is
 * intentionally disabled for the authenticated application.
 */
export const initSentry = () => {
  const dsn = import.meta.env['VITE_SENTRY_DSN'];
  const environment = import.meta.env['VITE_SENTRY_ENVIRONMENT'] || 'development';
  const release = import.meta.env['VITE_SENTRY_RELEASE'] || 'unknown';

  // Skip initialization if DSN not configured
  if (!dsn) {
    console.warn('[Sentry] DSN not configured, skipping initialization');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,

    // PII scrubbing (GDPR compliance)
    beforeSend: (event, hint) => scrubbedBeforeSend(event, hint) as Sentry.ErrorEvent | PromiseLike<Sentry.ErrorEvent | null> | null,

    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      browserTracingIntegration({
        // Trace navigation in TanStack Router
        // Note: TanStack Router navigation is automatically tracked via browserTracingIntegration
        enableLongTask: true,
        enableInp: true,
      }),
    ],

    // Performance monitoring
    tracesSampleRate: 0.1, // 10% of transactions
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/[a-z]+\.convex\.cloud/, // Convex API
      /^https:\/\/api\.mollie\.com/, // Mollie API
    ],

    // Ignore common non-actionable errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'canvas.contentDocument',
      // Network errors (user closes tab, loses connection)
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      // React dev tools
      '__REACT_DEVTOOLS_GLOBAL_HOOK__',
    ],

    // Deny URLs (don't track errors from external scripts)
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      // Social media widgets
      /graph\.facebook\.com/i,
      /connect\.facebook\.net/i,
    ],
  });

  console.log('[Sentry] Initialized', { environment, release });
};

// Export Sentry for use in components
export { Sentry };
